import React, { useState } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Mail, Settings, Bell, FileText, Save, RefreshCw, Shield, Sparkles, Zap, Database, Palette, Briefcase, Radio } from 'lucide-react';
import { MerchantEmailTemplates } from '@/components/settings/MerchantEmailTemplates';
import { MerchantApplicationSettings as MerchantFieldSettings } from '@/components/settings/MerchantApplicationSettings';
import { MerchantApplicationErrorBoundary } from '@/features/merchant-application/components/MerchantApplicationErrorBoundary';
import { IndustryClassificationSettings } from '@/components/settings/IndustryClassificationSettings';
import { ApplicationSourcesManagement } from '@/components/settings/ApplicationSourcesManagement';
import { cn } from '@/lib/utils';

export default function Config() {
  const { isSuperAdmin, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('email-templates');
  const [hasChanges, setHasChanges] = useState(false);
  const merchantSettingsRef = React.useRef<{ save: () => Promise<void> }>(null);

  const tabs = [
    {
      id: 'email-templates',
      label: 'Email Templates',
      icon: Mail,
      description: 'Customize email communications',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'merchant-fields',
      label: 'Field Configuration',
      icon: FileText,
      description: 'Manage application form fields',
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'industry-classification',
      label: 'Industry Classification',
      icon: Briefcase,
      description: 'Manage industry codes',
      color: 'from-green-500 to-green-600'
    },
    {
      id: 'application-sources',
      label: 'Application Sources',
      icon: Radio,
      description: 'Manage application sources',
      color: 'from-teal-500 to-teal-600'
    },
    {
      id: 'general',
      label: 'General Settings',
      icon: Settings,
      description: 'Configure system preferences',
      color: 'from-gray-500 to-gray-600'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      description: 'Manage alert preferences',
      color: 'from-amber-500 to-amber-600'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive" className="bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            You don't have permission to access this page. Only super admins can manage merchant application settings.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <MerchantApplicationErrorBoundary level="feature">
        {/* Modern Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200/50">
          <div className="px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl shadow-lg shadow-indigo-500/20">
                  <Settings className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Configuration Center</h1>
                  <p className="text-gray-500 text-sm mt-0.5">Manage merchant application settings and preferences</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {hasChanges && (
                  <Badge variant="secondary" className="bg-amber-100 text-amber-700 border-0">
                    <Zap className="h-3 w-3 mr-1" />
                    Unsaved changes
                  </Badge>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="border-gray-200 hover:bg-gray-50"
                  disabled={!hasChanges}
                  onClick={() => {
                    // Reset functionality would require storing original state
                    window.location.reload();
                  }}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reset
                </Button>
                <Button 
                  className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white shadow-lg shadow-indigo-600/20"
                  disabled={!hasChanges}
                  onClick={async () => {
                    if (activeTab === 'merchant-fields' && merchantSettingsRef.current) {
                      await merchantSettingsRef.current.save();
                    }
                  }}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "relative group bg-white rounded-xl p-4 border-2 transition-all duration-300",
                  isActive 
                    ? "border-indigo-500 shadow-lg shadow-indigo-500/10" 
                    : "border-gray-200 hover:border-gray-300 hover:shadow-md"
                )}
              >
                <div className="flex flex-col items-start gap-3">
                  <div className={cn(
                    "p-2 rounded-lg transition-all duration-300",
                    isActive 
                      ? `bg-gradient-to-br ${tab.color} shadow-md` 
                      : "bg-gray-100 group-hover:bg-gray-200"
                  )}>
                    <Icon className={cn(
                      "h-5 w-5 transition-colors",
                      isActive ? "text-white" : "text-gray-600"
                    )} />
                  </div>
                  <div className="text-left">
                    <h3 className={cn(
                      "font-medium transition-colors",
                      isActive ? "text-gray-900" : "text-gray-700"
                    )}>
                      {tab.label}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">{tab.description}</p>
                  </div>
                </div>
                {isActive && (
                  <div className="absolute inset-x-0 -bottom-px h-0.5 bg-gradient-to-r from-indigo-500 to-indigo-600" />
                )}
              </button>
            );
          })}
        </div>

        {/* Content Area */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200/50">
          <div className="p-6">
            {activeTab === 'email-templates' && (
              <MerchantApplicationErrorBoundary level="component">
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-4 border-b">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Mail className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">Email Templates</h2>
                        <p className="text-sm text-gray-500">Customize merchant application email communications</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-0">
                      <Sparkles className="h-3 w-3 mr-1" />
                      AI-Powered
                    </Badge>
                  </div>
                  <MerchantEmailTemplates />
                </div>
              </MerchantApplicationErrorBoundary>
            )}
            
            {activeTab === 'merchant-fields' && (
              <MerchantApplicationErrorBoundary level="component">
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-4 border-b">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <FileText className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">Field Configuration</h2>
                        <p className="text-sm text-gray-500">Control which fields appear in merchant applications</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="bg-purple-100 text-purple-700 border-0">
                      <Database className="h-3 w-3 mr-1" />
                      Dynamic Forms
                    </Badge>
                  </div>
                  <MerchantFieldSettings
                    ref={merchantSettingsRef}
                    onSettingsChange={setHasChanges}
                    onSave={() => setHasChanges(false)}
                  />
                </div>
              </MerchantApplicationErrorBoundary>
            )}

            {activeTab === 'industry-classification' && (
              <MerchantApplicationErrorBoundary level="component">
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-4 border-b">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Briefcase className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">Industry Classification</h2>
                        <p className="text-sm text-gray-500">Manage PayArc industry codes for merchant applications</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="bg-green-100 text-green-700 border-0">
                      <Shield className="h-3 w-3 mr-1" />
                      PayArc MCC
                    </Badge>
                  </div>
                  <IndustryClassificationSettings
                    onSettingsChange={setHasChanges}
                    onSave={() => setHasChanges(false)}
                  />
                </div>
              </MerchantApplicationErrorBoundary>
            )}

            {activeTab === 'application-sources' && (
              <MerchantApplicationErrorBoundary level="component">
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-4 border-b">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-teal-100 rounded-lg">
                        <Radio className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">Application Sources</h2>
                        <p className="text-sm text-gray-500">Manage merchant application sources and tracking</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="bg-teal-100 text-teal-700 border-0">
                      <Database className="h-3 w-3 mr-1" />
                      Source Management
                    </Badge>
                  </div>
                  <ApplicationSourcesManagement />
                </div>
              </MerchantApplicationErrorBoundary>
            )}

            {activeTab === 'general' && (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="p-4 bg-gray-100 rounded-full mb-4">
                  <Settings className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">General Settings</h3>
                <p className="text-gray-500 text-center max-w-sm">
                  General configuration options are coming soon. Check back later for system-wide preferences.
                </p>
                <Button variant="outline" className="mt-6" disabled>
                  <Shield className="h-4 w-4 mr-2" />
                  Coming Soon
                </Button>
              </div>
            )}
            
            {activeTab === 'notifications' && (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="p-4 bg-amber-100 rounded-full mb-4">
                  <Bell className="h-8 w-8 text-amber-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Notification Settings</h3>
                <p className="text-gray-500 text-center max-w-sm">
                  Configure how and when you receive notifications about merchant applications.
                </p>
                <Button variant="outline" className="mt-6" disabled>
                  <Palette className="h-4 w-4 mr-2" />
                  Coming Soon
                </Button>
              </div>
            )}
          </div>
        </div>
      </MerchantApplicationErrorBoundary>
    </div>
  );
}